import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { APIResponse, PagedResult } from 'models/api-response.model';
import { toast } from "react-toastify";
import { Page, Section, Question, QuestionnaireModel, PHQVersion, SnapShot, QuestionnaireCopyModel, QuestionOption, Validation, ValidationToUpdate, ValidationSearchParams } from 'models/questionnaire.model';
import { DomainCopyRequest, DomainPHQConfig, PHQDomain } from 'models/domain.model';
import { MessageTemplate } from 'models/message-template.model';
import { getLoginStorage, removeLoginStorage, setLoginStorage } from './storageLoginInfo';
import { HttpStatusCodeEnum } from 'models/http-status-code.enum';
import { PatientPHQSearchResponse } from 'models/patient-phq/patient-phq.model';

const makeToast = (msg: string) => {
    toast.error(msg, {
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    });
}

const instance = axios.create({
    crossdomain: true,
    baseURL: process.env.REACT_APP_API_URL,
} as AxiosRequestConfig);


let refreshQueue: any[] = [];
let refreshInProgress = false;

const callRefreshToken = async (): Promise<string> => {
    const loginStorage = await getLoginStorage();
    const { AccessToken, RefreshToken } = loginStorage?.loginResult;

    return instance.post("refresh-token", { AccessToken, RefreshToken })
        .then(async res => {
            if (res) {
                const { AccessToken: newAToken, RefreshToken: newRToken } = res.data.result;

                loginStorage.loginResult.AccessToken = newAToken;
                loginStorage.loginResult.RefreshToken = newRToken;

                // console.log("GOT NEW TOKENS", res.data.result)

                await setLoginStorage(loginStorage);

                return newAToken;
            }
        })
}

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const {
            config: originalRequest,
            response: { status }
        } = error;

        if (status === HttpStatusCodeEnum.FORBIDDEN) {
            console.error(error.response.data.message);
            makeToast("Not authorized.");
            return Promise.reject("Not authorized");
        }

        if (status === HttpStatusCodeEnum.UNAUTHORIZED) {
            if (originalRequest.url === 'refresh-token') {
                makeToast("You are signed out. Please login");

                if (window.location.pathname !== '/login') {
                    await removeLoginStorage();
                    window.location.href = "login";
                }

                return Promise.reject("You are signed out. Please login.");
            }

            if (originalRequest.url !== 'refresh-token') {
                if (!refreshInProgress) {
                    refreshInProgress = true;

                    callRefreshToken()
                        .then(token => {
                            instance.defaults.headers.Authorization = `Bearer ${token}`;
                            refreshQueue.forEach((v) => v.resolve(token));
                        })
                        .catch(err => {
                            console.error('refresh token failed', { err });
                            refreshQueue.forEach((v) => v.reject(err));
                        })
                        .finally(() => {
                            refreshInProgress = false;
                            refreshQueue = [];
                            window.location.href = "login";
                        })
                }
            }

            return new Promise((resolve, reject) => {
                refreshQueue.push({
                    resolve: (token: string) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(instance.request(originalRequest))
                    },
                    reject: (err: any) => {
                        reject(err)
                    },
                })
            })
        }

        const message = error.response.data?.message ?? "An error has occurred";
        makeToast(message);
        return Promise.reject(message);
    }
);


const getPerDomainInstance = async () => {
    const loginInfo = await getLoginStorage();

    return axios.create({
        baseURL: loginInfo.domain.ServiceUrl
    });
}

export { instance, getPerDomainInstance };

export const exportForm = (PHQID: number) => {
    return instance.get(`phq/${PHQID}/export`, {
        responseType: "blob",
    });
}

export const importForm = (formData: FormData) => {
    return instance.post('phq/import', formData);
}

export const getPHQByEnvironment = (domain: string) => {
    return instance.get("phq", { params: { domain } });
}

export const getPHQById = (PHQID: number): Promise<AxiosResponse<APIResponse<QuestionnaireModel>>> => {
    return instance.get(`phq/${PHQID}`);
}

export const createPHQ = (phq: QuestionnaireModel) => {
    return instance.post("phq", phq);
}

export const copyPHQ = (phqCopy: QuestionnaireCopyModel) => {
    return instance.post(`phq/${phqCopy.PHQID}/copy`, phqCopy);
}

export const updatePHQ = (phq: QuestionnaireModel) => {
    return instance.put(`phq/${phq.PHQID}`, phq);
}

export const deletePHQ = (id: number) => {
    return instance.delete(`phq/${id}`);
}

export const createQuestion = (question: Question) => {
    return instance.post("questions", question);
}

export const updateQuestion = (question: Question) => {
    return instance.put(`questions/${question.QuestionID}`, question);
}

export const deleteQuestion = (questionID: number) => {
    return instance.delete(`questions/${questionID}`);
}

export const createSection = (section: Section) => {
    return instance.post("sections", section);
}

export const updateSection = (section: Section) => {
    return instance.put(`sections/${section.SectionID}`, section);
}

export const deleteSection = (sectionID: number) => {
    return instance.delete(`sections/${sectionID}`);
}

export const reorderSections = (sections: Section[]) => {
    return instance.put("sections/reorder", sections);
}

/**
 * Returns full version (with RawPHQ)
 * @param versionId 
 */
export const getVersion = (versionId: number): Promise<AxiosResponse<APIResponse<PHQVersion>>> => {
    return instance.get(`versions/${versionId}`);
}

export const restoreVersion = (versionId: number) => {
    return instance.post(`versions/${versionId}/restore`);
}

export const getValidationList = (params: ValidationSearchParams): Promise<AxiosResponse<APIResponse<Validation[]>>> => {
    return instance.get("validations", { params });
}

export const createNewValidation = (validation: Validation) => {
    return instance.post("validations", validation);
}
export const updateValidation = (validationToUpdate: ValidationToUpdate) => {
    return instance.put("validations", validationToUpdate);
}

export const restoreSnapshot = (snashotId: number) => {
    return instance.post(`snapshots/${snashotId}/restore`);
}

export const createNewVersion = (phqVersion: PHQVersion) => {
    return instance.post("versions", phqVersion);
}

export const searchSnapshots = (params: object): Promise<AxiosResponse<APIResponse<SnapShot[]>>> => instance.get("snapshots", { params });

export const searchPages = (params: object): Promise<AxiosResponse<APIResponse<Page[]>>> => instance.get("pages", { params })

export const searchSections = (params: object): Promise<AxiosResponse<APIResponse<Section[]>>> => instance.get("sections", { params })

/**
 * Returns shallow versions (no RawPHQ).
 * Call getVersion() to get RawPQH
 * @param params query params
 */
export const searchVersions = (params: object): Promise<AxiosResponse<APIResponse<PHQVersion[]>>> => instance.get("versions", { params })

export const createPHQPage = (page: Page) => {
    return instance.post("pages", page);
}

export const updatePHQPage = (page: Page) => {
    return instance.put(`pages/${page.PageID}`, page);
}

export const reorderPages = (pages: Page[]) => {
    return instance.put("pages/reorder", pages);
}

export const deletePHQPage = (id: number) => {
    return instance.delete(`pages/${id}`);
}

export const reorderQuestions = (questions: Question[]) => {
    return instance.put("questions/reorder", questions);
}

export const createQuestionOption = (qo: QuestionOption) => {
    return instance.post(`questions/${qo.QuestionID}/options`, qo);
}

export const deleteQuestionOption = (questionId: number, id: number) => {
    return instance.delete(`questions/${questionId}/options/${id}`);
}

export const reorderQuestionOptions = (questionId: number, options: QuestionOption[]) => {
    return instance.put(`questions/${questionId}/options/reorder`, options);
}

export const getDomains = () => {
    return instance.get("/domains");
}

export const createDomain = (domain: PHQDomain) => {
    return instance.post("/domains", domain);
}

export const updateDomain = (domain: PHQDomain) => {
    return instance.put(`/domains/${domain.DomainID}`, domain);
}

export const copyDomain = (domain: DomainCopyRequest) => {
    return instance.post('/domains/copy', domain);
}

export const deleteDomain = (id: number) => {
    return instance.delete(`/domains/${id}`);
}

export const generateKeys = () => {
    return instance.post('/domains/generate-keys');
}

export const getTemplates = () => {
    return instance.get("/templates");
}

export const getTemplatePlaceholders = () => {
    return instance.get("/templates/placeholders");
}

export const getTemplateById = (id: number) => {
    return instance.get(`/templates/${id}`);
}

export const createTemplate = (template: MessageTemplate) => {
    return instance.post("/templates", template);
}

export const updateTemplate = (template: MessageTemplate) => {
    return instance.put(`/templates/${template.MessageTemplateID}`, template);
}

export const deleteTemplate = (id: number) => {
    return instance.delete(`/templates/${id}`);
}

export const getLookup = async () => {
    const domainInstance = await getPerDomainInstance();
    return domainInstance.get('/integrationapi/Lookup');
}

export const searchPatientForms = async (params: object): Promise<AxiosResponse<APIResponse<PagedResult<PatientPHQSearchResponse>>>> => {
    return instance.get("/patientphqs", { params });
}

export const getDomainPHQConfig = async (domainId: number): Promise<AxiosResponse<APIResponse<DomainPHQConfig>>> => {
    return instance.get(`domains/${domainId}/phq-config`);
}